import { Eye } from "lucide-react";
import { DirectorKpiGrid } from "@/components/director/DirectorKpiGrid";
import { DirectorTeamComparison } from "@/components/director/DirectorTeamComparison";
import { DirectorTopRisks } from "@/components/director/DirectorTopRisks";
import { DirectorDataQuality } from "@/components/director/DirectorDataQuality";
import { DirectorHeaderFilter } from "@/components/director/DirectorHeaderFilter";
import type { DirectorSummary } from "@/api/types";

/**
 * S5 — Director Home (D-081, Hybrid A+C). Org-wide READ-ONLY business surface
 * rendered at /today for director / admin_data_all / super-admin (C2 gate). 4
 * blocks: Business-KPI · Team comparison · Top-risks · Data-quality. NO write/
 * action-CTA (DirectorAction OFF, D2=C); «Переглянути» = link drilldown. DS bound
 * to live globals.css (--primary 158); mockup v3.
 */
export function DirectorHome({ summary }: { summary: DirectorSummary }) {
  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1320px] space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Головна директора — бізнес, команди, ризики</h1>
            <p className="mt-0.5 flex items-center gap-2.5 text-sm text-muted-foreground">
              org-wide · director / admin
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
                <Eye className="h-3 w-3" /> Тільки перегляд
              </span>
            </p>
          </div>
          <DirectorHeaderFilter filter={summary.orgFilter} />
        </header>

        <section>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Бізнес-KPI</h2>
          <DirectorKpiGrid kpis={summary.kpis} />
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Порівняння команд</h2>
          <DirectorTeamComparison rows={summary.teamComparison} />
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Топ-ризики</h2>
          <DirectorTopRisks risks={summary.topRisks} />
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Якість даних</h2>
          <DirectorDataQuality noOwner={summary.dataQuality.noOwnerClients} />
        </section>
      </div>
    </div>
  );
}

export function DirectorHomeSkeleton() {
  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1320px] space-y-6">
        <div className="h-12 w-80 animate-pulse rounded bg-muted" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
        <div className="h-48 animate-pulse rounded-2xl bg-muted" />
        <div className="h-56 animate-pulse rounded-2xl bg-muted" />
      </div>
    </div>
  );
}
